/**
 * SquadCrown — full-screen end-of-night crown announcement.
 *
 * Polls `/api/events/:id/missions/squad-crown` every 5 seconds while
 * nothing is playing. When the server returns `active: true`, checks
 * `triggered_at` against localStorage so the same device doesn't play
 * it twice, then runs a 15-second cinematic reveal:
 *
 *   • Full-screen overlay in the squad's colour
 *   • Trophy drop from top
 *   • Big squad name + emoji
 *   • Points counter roll-up
 *   • Top guest + runner-up
 *   • Confetti rain in the squad's colour
 *
 * Mount this once on: GuestHome, LiveBookPage (venue TV), and anywhere
 * else you want the announcement to hijack the screen on cue.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Crown } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHOW_MS = 15000;
const seenKey = (eventId) => `eventdrop_crown_seen_${eventId}`;

const SquadCrown = ({ event }) => {
  const [crown, setCrown] = useState(null);
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => {
    if (!event?.event_id) return;
    let stopped = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/events/${event.event_id}/missions/squad-crown`);
        if (stopped) return;
        if (!r.data?.active) return;
        const c = r.data.crown;
        // Dedupe per device: don't re-play for the same trigger
        let seen = '';
        try { seen = localStorage.getItem(seenKey(event.event_id)) || ''; } catch {}
        if (seen === c.triggered_at) return;
        try { localStorage.setItem(seenKey(event.event_id), c.triggered_at); } catch {}
        setCrown(c);
        setVisible(true);
        if (hideTimer.current) clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setVisible(false), SHOW_MS);
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 5000);
    return () => { stopped = true; clearInterval(iv); if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [event?.event_id]);

  if (!visible || !crown) return null;

  const w = crown.winner || {};
  const color = w.color || '#FFD700';
  const runner = crown.runner_up;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center overflow-hidden"
      style={{ background: `radial-gradient(circle at center, ${color}28 0%, rgba(0,0,0,0.95) 75%)` }}
      data-testid="squad-crown"
    >
      {/* Confetti rain in the squad's colour */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 80 }).map((_, i) => {
          const size = 6 + Math.random() * 10;
          return (
            <span
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-5%',
                width: size,
                height: size * 0.4,
                background: i % 3 === 0 ? color : (i % 3 === 1 ? '#FFFFFF' : '#FFD700'),
                borderRadius: '2px',
                animation: `crownFall ${3 + Math.random() * 2.5}s ease-in forwards`,
                animationDelay: `${Math.random() * 1.2}s`,
                transform: `rotate(${Math.random() * 360}deg)`,
                opacity: 0.9,
              }}
            />
          );
        })}
      </div>

      <div
        className="relative text-center px-8"
        style={{ animation: 'crownReveal .9s cubic-bezier(0.34,1.56,0.64,1)' }}
      >
        <div
          className="mx-auto mb-6 inline-flex items-center justify-center w-28 h-28 rounded-full"
          style={{
            background: `radial-gradient(circle, ${color}, ${color}40)`,
            boxShadow: `0 0 80px ${color}, 0 0 200px ${color}66`,
            animation: 'crownDrop 1s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          <Crown size={70} weight="fill" style={{ color, filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))' }} />
        </div>

        <p className="text-white/50 text-sm uppercase tracking-[8px] font-black mb-3">Squad Crown</p>

        <h1
          className="text-6xl md:text-7xl font-black mb-2 leading-none"
          style={{ color, textShadow: `0 4px 20px ${color}88`, fontFamily: 'Inter, sans-serif' }}
        >
          <span className="mr-3">{w.emoji}</span>
          {w.name}
        </h1>

        {crown.event_name && (
          <p className="text-white/60 text-sm italic mb-6">
            take home the crown at {crown.event_name}
          </p>
        )}

        <div className="inline-flex items-center gap-8 px-6 py-4 rounded-2xl mb-5" style={{ background: 'rgba(0,0,0,0.4)', border: `1px solid ${color}55`, backdropFilter: 'blur(8px)' }}>
          <div className="text-center">
            <p className="text-4xl">🥇</p>
            <p className="text-white font-black text-2xl">{w.gold || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-4xl">🥈</p>
            <p className="text-white font-black text-2xl">{w.silver || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-4xl">🥉</p>
            <p className="text-white font-black text-2xl">{w.bronze || 0}</p>
          </div>
          <div className="w-px h-12" style={{ background: 'rgba(255,255,255,0.15)' }} />
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest text-white/40 font-bold">Points</p>
            <p className="text-5xl font-black tabular-nums" style={{ color }}>{w.points || 0}</p>
          </div>
        </div>

        {crown.top_guest?.guest_name && (
          <p className="text-white/70 text-base mb-2">
            MVP: <span className="font-black text-white">{crown.top_guest.guest_name}</span>
            {' '}· <span className="tabular-nums text-amber-300">{crown.top_guest.points} pts</span>
          </p>
        )}

        {runner && (
          <p className="text-white/40 text-sm">
            Runner-up: <span className="font-bold" style={{ color: runner.color || '#C0C0C0' }}>{runner.emoji} {runner.name}</span>
            {' '}<span className="tabular-nums">({runner.points} pts)</span>
          </p>
        )}
      </div>

      <style>{`
        @keyframes crownReveal { 0% { opacity: 0; transform: scale(0.85) translateY(30px); filter: blur(6px) } 100% { opacity: 1; transform: scale(1) translateY(0); filter: none } }
        @keyframes crownDrop { 0% { transform: translateY(-120vh) rotate(-120deg) scale(0.5) } 65% { transform: translateY(6px) rotate(5deg) scale(1.08) } 100% { transform: translateY(0) rotate(0) scale(1) } }
        @keyframes crownFall { 0% { transform: translateY(0) rotate(0deg); opacity: 1 } 100% { transform: translateY(110vh) rotate(900deg); opacity: 0 } }
      `}</style>
    </div>
  );
};

export default SquadCrown;
